/* eslint-disable max-len */
import React, { useCallback, useMemo } from 'react';
import { useSessionStorage } from 'react-use';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { FontFamilyInfo } from '../../../platform/assets/FontFamilyInfo';
import { HandwritingFontList, SansFontList, SerifFontList } from '../../../platform/assets/InAppFontList';
import { ColorButton } from '../../../ui/ColorButton';
import { KeepPopoutWidgetOpenButton } from '../../../ui/KeepOpenButton';
import { Position } from '../../../ui/Position';
import { useResolve } from '../../Container';
import { toolEquip } from '../../canvas/tooling/ToolCommands';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { ElementSelector } from '../../canvas/ui/ElementSelector';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Colors } from './../../../ui';

const { ItemGroup, ItemSet, Item, ItemPreview } = ElementSelector;

const TextFrame = styled.div`
    width: 100%;
    font-size: 1.25rem;
    text-align: left;
`;

const special = 'Swanky and Moo Moo';
const title = (font: FontFamilyInfo) =>
    font.name === special ? 'Yes, it\'s really called that!' : null;

const TextPreview = React.memo<{ font: FontFamilyInfo, color: string, active?: boolean }>(({ font, color, active }) => {
    return <TextFrame title={title(font)} style={{ fontFamily: font.name, color }}>
        {font.label}
        {active && <Position at="right">
            <Icon src="deqo://icons/hero/check" />
        </Position>}
    </TextFrame>;
});

const id = (...x: string[]) => x.join('/');

const TextItem = (font: FontFamilyInfo, color: string, active?: boolean) => (
    Item(id(font.name), null, { font, fontName: font.name, color, active }, ItemPreview(TextPreview))
);

const TextIcon = styled.div<{ font: string }>`
    font-family: ${p => p.font};
    font-size: 1.25rem;
    font-weight: 450;
`;

const Serif = () => <TextIcon font="Times New Roman">Aa</TextIcon>;
const Sans = () => <TextIcon font="Inter">Aa</TextIcon>;
const Hand = () => <TextIcon font="Architects Daughter">Aa</TextIcon>;

const createModel = (color: string, active: string) => [
    ItemGroup('sans', 'Sans', Sans, [
        ItemSet('sans', 'Sans', 1, SansFontList.map(font => TextItem(font, color, font.name === active))),
    ]),
    ItemGroup('serif', 'Serif', Serif, [
        ItemSet('serif', 'Serif', 1, SerifFontList.map(font => TextItem(font, color, font.name === active))),
    ]),
    ItemGroup('handwriting', 'Handwriting', Hand, [
        ItemSet('handwriting', 'Handwriting', 1, HandwritingFontList.map(font => TextItem(font, color, font.name === active))),
    ]),
];

export const TextWidget = React.memo<{ widget: PopoutWidget }>(({ widget }) => {

    const dispatch = useResolve(CommandDispatcher);
    const tools = useResolve(ToolManager);
    const [color, setColor] = useSessionStorage('deqo/ui/textWidget/color', Colors.text);
    const active = tools.getSetting('textTool', 'fontName');

    const onSelect = useCallback((item: any, selectMode: 'drag' | 'click') => {
        widget.update({ open: !widget.autoClose });
        const mode = selectMode === 'click' ? 'drag' : 'drop';

        tools.setSettings('textTool', { ...item.props, color });
        dispatch(toolEquip, ['textTool', `${mode}:edit`]);

    }, [color]);

    const model = useMemo(() => createModel(color ?? Colors.text, active), [color, active]);

    return <Flexer relative>
        <ElementSelector name="text" model={model} onSelectItem={onSelect} />
        <Position inset=".6" at="topRight">
            <Flexer flex="row" align="center" gap=".5">
                <ColorButton value={color} onChange={setColor} />
                <KeepPopoutWidgetOpenButton widget={widget} />
            </Flexer>
        </Position>
    </Flexer>;
});
